import { FC, useState } from "react"
import { useHistory } from "react-router-dom"
import { css, Theme } from "@emotion/react"
import { SupportIssueModal } from "~/shared-components"
import { LoginModalEcomm } from "~/views/auth/login"
import { useQueryClient } from "react-query"
import { useShopContext } from "~/contexts"
import { APPLICATION, useAuth, useIsUser, UserId, useUserCanUse } from "@ncs/ncs-api"
import {
	AnimatedEntrance,
	Box,
	Button,
	Paragraph,
	useScreenSizeMatch,
	TrackingEvent,
	Icon,
	ButtonEcomm,
} from "@ncs/web-legos"
import { MobileAccountMenuModal } from "../MobileAccountMenuModal"
import { useAnalytics } from "~/hooks/useAnalytics"

export const ShopBarUserWelcomeEcomm: FC = () => {
	const { user, logout } = useAuth()
	const history = useHistory()
	const [showTicketModal, setShowTicketModal] = useState(false)
	const screenIsMediumUp = useScreenSizeMatch("md", "up")
	const canUseConnectivity = useUserCanUse(APPLICATION.Connectivity)
	const canUseEquipment = useUserCanUse(APPLICATION.Equipment)
	const canUseDispatch = useUserCanUse([APPLICATION.CreateDispatch, APPLICATION.SearchDispatch])
	const isDb = useIsUser(UserId.DrivenBrands)
	const [isLoginOpen, setIsLoginOpen] = useState(false)
	const [showAcountSubmenu, setShowAcountSubmenu] = useState(false)
	const [showModalAcountSubmenu, setShowModalAcountSubmenu] = useState(false)
	const queryClient = useQueryClient()
	const [, shopDispatch] = useShopContext()
	const { resetAnalytics, trackEvent } = useAnalytics()

	const handleMouseEnter = () => {
		if (screenIsMediumUp) {
			setShowAcountSubmenu(true)
		}
	}

	const accountMenuHandler = () => {
		if (!screenIsMediumUp) {
			setShowModalAcountSubmenu(true)
		}
	}

	const closeAccountModalHandler = () => {
		if (!screenIsMediumUp) {
			setShowModalAcountSubmenu(false)
		}
	}

	const loginHandler = () => {
		setIsLoginOpen(true)
		trackEvent(TrackingEvent.LOGIN_VIEW)
	}

	const onLoginClose = () => {
		setIsLoginOpen(false)
	}

	const logoutHandler = () => {
		shopDispatch({
			type: "reset checkout state",
		})
		logout()
		queryClient.removeQueries()
		resetAnalytics()
		history.push("/shop")
		trackEvent(TrackingEvent.LOG_OUT)
	}

	const contactUsHandler = () => {
		setShowTicketModal(true)
		trackEvent(TrackingEvent.CONTACT_US)
	}

	const connectivityHandler = () => {
		history.push("/connectivity")
		trackEvent(TrackingEvent.CONNECTIVITY)
	}

	const myEquipmentHandler = () => {
		history.push("/my-equipment")
		trackEvent(TrackingEvent.MY_EQUIPMENT)
	}

	const myServiceRequestsHandler = () => {
		history.push("/service-requests")
		trackEvent(TrackingEvent.MY_SERVICE_REQUESTS)
	}

	const myAccountHandler = () => {
		history.push("/account")
		trackEvent(TrackingEvent.MY_ACCOUNT)
	}

	return (
		<>
			<LoginModalEcomm isOpen={isLoginOpen} onClose={onLoginClose} />
			<SupportIssueModal
				isOpen={showTicketModal}
				onClose={() => setShowTicketModal(false)}
			/>
			{user ?
				<Box display="flex" alignItems="center">
					<div onMouseEnter={handleMouseEnter}>
						<span css={userWelcomeStyle}>
							<Box display="flex" alignItems="center" columnGap={0.75}>
								<ButtonEcomm
									icon="circle-user"
									onClick={accountMenuHandler}
									css={userIconStyle}
									aria-label="User account menu handler"
								/>
								{screenIsMediumUp && (
									<Box display="flex" flexDirection="column" flexShrink={0}>
										<Paragraph bold small color="white" css={firstnameStyle}>
											{user.name.split(" ")[0]}
										</Paragraph>
									</Box>
								)}
							</Box>
						</span>
					</div>
					<div
						onMouseLeave={() => {
							setShowAcountSubmenu(false)
						}}
					>
						<AnimatedEntrance
							show={showAcountSubmenu}
							css={submenuContainerStyle}
							duration={200}
							delay={200}
						>
							{user && <div css={usernameStyle}>{user.name}</div>}
							<ul css={accountMenuStyle}>
								{!isDb && (
									<li css={separatorStyle}>
										<button onClick={myAccountHandler} css={buttonStyle}>
											My Account
										</button>
									</li>
								)}
								{!isDb && canUseDispatch && (
									<li>
										<button
											onClick={myServiceRequestsHandler}
											css={buttonStyle}
										>
											My Service Requests
										</button>
									</li>
								)}
								{!isDb && canUseEquipment && (
									<li>
										<button onClick={myEquipmentHandler} css={buttonStyle}>
											My Equipment
										</button>
									</li>
								)}
								{canUseConnectivity && (
									<li>
										<button onClick={connectivityHandler} css={buttonStyle}>
											Connectivity
										</button>
									</li>
								)}
								<li css={separatorStyle}>
									<button onClick={contactUsHandler} css={buttonStyle}>
										Contact Us
									</button>
								</li>
								<li css={separatorStyle}>
									<button onClick={logoutHandler} css={buttonStyle}>
										<Icon icon="arrow-right-from-bracket" />
										Logout
									</button>
								</li>
							</ul>
						</AnimatedEntrance>
					</div>
				</Box>
			:	<Box display="flex" alignItems="center" onMouseEnter={handleMouseEnter}>
					<Button onClick={loginHandler}>
						<Paragraph bold small color="white" css={firstnameStyle}>
							Login / Signup
						</Paragraph>
					</Button>
				</Box>
			}
			<MobileAccountMenuModal
				isOpen={showModalAcountSubmenu}
				onClose={closeAccountModalHandler}
			/>
		</>
	)
}

const buttonStyle = css`
	background: none;
	border: none;
	padding: 0;
	margin: 0;
	font: inherit;
	color: inherit;
	cursor: pointer;
	outline: none;
	text-align: inherit;
	display: inline-block;
`

const userIconStyle = (theme: Theme) => css`
	cursor: pointer;
	display: inline-block;
	padding-left: 6px;
	padding-top: 4px;

	${theme.breakpoints.down("sm")} {
		margin-left: 2rem;
	}

	svg {
		width: 1.5rem;
		height: 1.5rem;
	}
`

const firstnameStyle = css`
	max-width: 9em;
	text-overflow: ellipsis;
	overflow: hidden;
	color: #fff;
	text-align: right;
	font-size: 16px;
	font-style: normal;
	font-weight: 700;
	line-height: normal;
	padding-top: 0px;
	padding-bottom: 0px;
	margin-top: 9px;
	margin-bottom: 11px;
`

const usernameStyle = css`
	padding-left: 24px;
	padding-top: 36px;
	padding-bottom: 13px;
	font-weight: 900;
	color: #111827;
	font-size: 16px;
	font-style: normal;
	font-weight: 600;
	line-height: normal;
`

const submenuContainerStyle = css`
	position: absolute;
	z-index: 999;
	top: 2rem;
	width: 278px;
	right: 0rem;
	box-shadow: 0 4px 4px rgba(0, 0, 0, 0.25);
	z-index: 999;
	background: white;
	border-radius: 8px;
	overflow: hidden;
	& * {
		overflow: hidden;
	}

	& li:last-child {
		padding-bottom: 24px;
	}
`

const userWelcomeStyle = css`
	float: right;
	flex-shrink: 0;
	cursor: pointer;
	span {
		font-size: 0.8rem;
		color: white;
		font-weight: normal;
	}
`

const separatorStyle = (theme: Theme) => css`
	border-top: 1px solid ${theme.palette.colors.divider};
`

const accountMenuStyle = (theme: Theme) => css`
	break-inside: avoid-column;

	li {
		a,
		button {
			display: flex;
			width: 100%;
			height: 40px;
			padding-left: 24px;
			padding-right: 24px;
			padding-top: 8px;
			padding-bottom: 14px;
			text-align: left;
			text-decoration: none;
			color: ${theme.palette.colors.gray900};
			font-size: 14px;
			font-weight: 400;
			line-height: normal;

			&:hover path {
				fill: white;
			}
			&:hover {
				background: ${theme.palette.colors.blue500};
				color: white;
			}
			svg {
				margin-right: 0.5em;
				margin-top: 4px;
			}
		}

		div {
			height: 18px;
		}
	}
`
